import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiClient } from '../services/apiClient'
import { logger } from '../services/logger'
import { useAppStore } from '../store/useAppStore'
import type {
  UserAuthResponse,
  SubscriptionResponse,
  CategoriesResponse,
  CardsResponse,
  AnnouncementsResponse,
  CardWithContentResponse,
  FavoritesResponse,
  FavoriteStatusResponse,
  UsersCountResponse,
  AddToFavoritesRequest,
  RemoveFromFavoritesRequest,
  CheckSpinResponse,
  PrizesResponse,
  MyPrizesResponse,
  SpinResponse,
  CoursesResponse,
  CourseDetailResponse,
  LessonDetailResponse
} from '../types/api'

/**
 * Определяет, нужно ли логировать ошибку подписки
 */
function shouldLogSubscriptionError(error: any): boolean {
  const status = error?.response?.status
  const detail = (error as any)?.response?.data?.detail
  
  // Не логируем ошибки, связанные с отсутствием активной подписки
  if (detail && (
    detail.includes('Активная подписка не найдена') ||
    detail.includes('подписка не найдена') ||
    detail.includes('subscription not found') ||
    detail.includes('Для просмотра избранного нужна активная подписка') ||
    detail.includes('Для доступа к категориям требуется активная подписка') ||
    detail.includes('активная подписка') ||
    detail.includes('требуется подписка') ||
    detail.includes('нужна подписка')
  )) {
    return false
  }
  
  // Не логируем 404 ошибки с сообщением об отсутствии подписки
  if (status === 404 && detail && (
    detail.includes('подписка не найдена') ||
    detail.includes('subscription not found') ||
    detail.includes('Активная подписка не найдена')
  )) {
    return false
  }
  
  // Логируем все остальные ошибки подписки
  return true
}

// === Пользователь ===

export const useAuth = () => {
  const { setSubscription, setAuthStatus } = useAppStore()
  const queryClient = useQueryClient()
  
  return useMutation<UserAuthResponse, Error, { telegramUserId: number, userId?: number }>(
    ({ telegramUserId, userId = 0 }) => {
      setAuthStatus('pending') // Устанавливаем статус "в процессе"
      return apiClient.auth(telegramUserId, userId)
    },
    {
      onSuccess: () => {
        setAuthStatus('success') // Устанавливаем статус "успешно"
        // Не перезаписываем данные пользователя, так как они уже установлены из Telegram WebApp
   
        
        // После успешной авторизации запрашиваем подписку пользователя
        queryClient.fetchQuery(
          ['subscription'],
          () => apiClient.getSubscription(),
          {
            retry: 1
          }
        ).then((subscriptionData: SubscriptionResponse) => {
          const subscription = {
            isActive: new Date(subscriptionData.subscription.subscription_end_timestamp) > new Date(),
            expiresAt: subscriptionData.subscription.subscription_end_timestamp,
            type: subscriptionData.subscription.subscription_type as 'monthly' | 'yearly'
          }
          setSubscription(subscription)
        }).catch(async (error) => {
          console.error('❌ Ошибка получения подписки:', error)
          
          // Проверяем, нужно ли логировать эту ошибку
          const shouldLog = shouldLogSubscriptionError(error)
          if (shouldLog) {
            // Логируем ошибку получения подписки
            await logger.logError(error, {
              method: 'GET',
              url: '/api/user/subscription',
              errorType: 'subscription_fetch_error',
              additionalData: {
                detail: (error as any)?.response?.data?.detail
              }
            })
          }
          
          // Если нет подписки, устанавливаем неактивную
          setSubscription({ isActive: false })
        })
      },
      onError: async (error) => {
        setAuthStatus('error') // Устанавливаем статус "ошибка"
        console.error('❌ useAuth: Ошибка авторизации:', error)
        // Логируем ошибку авторизации
        await logger.logError(error, {
          method: 'POST',
          url: '/api/user/auth',
          errorType: 'auth_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
        // При ошибке авторизации тоже устанавливаем неактивную подписку
        setSubscription({ isActive: false })
      }
    }
  )
}

export const useSubscription = () => {
  const { setSubscription, authStatus } = useAppStore()
  const isAuthComplete = authStatus === 'success'
  
  return useQuery<SubscriptionResponse, Error>(
    ['subscription'],
    () => apiClient.getSubscription(),
    {
      enabled: isAuthComplete, // Ждем завершения auth
      onSuccess: (data) => {
        const subscription = {
          isActive: new Date(data.subscription.subscription_end_timestamp) > new Date(),
          expiresAt: data.subscription.subscription_end_timestamp,
          type: data.subscription.subscription_type as 'monthly' | 'yearly'
        }
        setSubscription(subscription)
      },
      onError: async (error) => {
        console.error('Ошибка получения подписки:', error)
        
        // Проверяем, нужно ли логировать эту ошибку
        const shouldLog = shouldLogSubscriptionError(error)
        if (shouldLog) {
          // Логируем ошибку получения подписки
          await logger.logError(error, {
            method: 'GET',
            url: '/api/user/subscription',
            errorType: 'subscription_query_error',
            additionalData: {
              detail: (error as any)?.response?.data?.detail
            }
          })
        }
        
        // Если нет подписки, устанавливаем неактивную
        setSubscription({ isActive: false })
      },
      retry: 1
    }
  )
}

// === Контент ===

export const useCategories = (enabled: boolean = true) => {
  const { authStatus } = useAppStore()
  const isAuthComplete = authStatus === 'success' // Auth завершен успешно
  
  return useQuery<CategoriesResponse, Error>(
    ['categories'],
    () => apiClient.getCategories(),
    {
      enabled: enabled && isAuthComplete, // Ждем завершения auth
      staleTime: 5 * 60 * 1000, // 5 минут
      cacheTime: 10 * 60 * 1000, // 10 минут
      retry: 2,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: '/api/content/categories',
          errorType: 'categories_fetch_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

export const useCards = () => {
  const { authStatus } = useAppStore()
  const isAuthComplete = authStatus === 'success' // Auth завершен успешно
  
  return useQuery<CardsResponse, Error>(
    ['cards'],
    () => apiClient.getCards(),
    {
      enabled: isAuthComplete, // Ждем завершения auth
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 2,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: '/api/content/cards',
          errorType: 'cards_fetch_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

export const useAnnouncements = () => {
  const { authStatus } = useAppStore()
  const isAuthComplete = authStatus === 'success' // Auth завершен успешно
  
  return useQuery<AnnouncementsResponse, Error>(
    ['announcements'],
    () => apiClient.getAnnouncements(),
    {
      enabled: isAuthComplete, // Ждем завершения auth
      staleTime: 2 * 60 * 1000, // 2 минуты для анонсов
      cacheTime: 5 * 60 * 1000,
      retry: 2,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: '/api/content/announcements',
          errorType: 'announcements_fetch_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

export const useCard = (cardId: number | null) => {
  return useQuery<CardWithContentResponse, Error>(
    ['card', cardId],
    () => apiClient.getCard(cardId!),
    {
      enabled: !!cardId,
      staleTime: 10 * 60 * 1000, // 10 минут для отдельных карточек
      retry: 1,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: `/api/content/card/${cardId}`,
          errorType: 'card_fetch_error',
          additionalData: { 
            cardId,
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

export const useUsersCount = () => {
  const { user } = useAppStore()
  const isAuthComplete = !!user // Auth завершен, если пользователь установлен
  
  return useQuery<UsersCountResponse, Error>(
    ['usersCount'],
    () => apiClient.getUsersCount(),
    {
      enabled: isAuthComplete, // Ждем завершения auth
      staleTime: 30 * 60 * 1000, // 30 минут
      cacheTime: 60 * 60 * 1000, // 1 час
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: '/api/content/users-count',
          errorType: 'users_count_fetch_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

// === Избранное ===

export const useFavorites = () => {
  const { authStatus } = useAppStore()
  const isAuthComplete = authStatus === 'success' // Auth завершен успешно
  
  return useQuery<FavoritesResponse, Error>(
    ['favorites'],
    () => apiClient.getFavorites(),
    {
      enabled: isAuthComplete, // Ждем завершения auth
      onSuccess: (data) => {
        // Обновляем локальное состояние избранного
        useAppStore.setState({ favorites: data.favorite_ids.map(String) })
      },
      staleTime: 2 * 60 * 1000,
      retry: 1,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: '/api/user/favorites',
          errorType: 'favorites_fetch_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

export const useAddToFavorites = () => {
  const queryClient = useQueryClient()
  const { toggleFavorite } = useAppStore()
  
  return useMutation<FavoriteStatusResponse, Error, AddToFavoritesRequest>(
    (request) => apiClient.addToFavorites(request),
    {
      onSuccess: (data, variables) => {
        if (data.success) {
          // Обновляем локальное состояние
          toggleFavorite(String(variables.card_id))
          // Инвалидируем кэш избранного
          queryClient.invalidateQueries(['favorites'])
        }
      },
      onError: async (error) => {
        console.error('Ошибка добавления в избранное:', error)
        await logger.logError(error, {
          method: 'POST',
          url: '/api/user/favorites/add',
          errorType: 'add_favorite_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

export const useRemoveFromFavorites = () => {
  const queryClient = useQueryClient()
  const { toggleFavorite } = useAppStore()
  
  return useMutation<FavoriteStatusResponse, Error, RemoveFromFavoritesRequest>(
    (request) => apiClient.removeFromFavorites(request),
    {
      onSuccess: (data, variables) => {
        if (data.success) {
          // Обновляем локальное состояние
          toggleFavorite(String(variables.card_id))
          // Инвалидируем кэш избранного
          queryClient.invalidateQueries(['favorites'])
        }
      },
      onError: async (error) => {
        console.error('Ошибка удаления из избранного:', error)
        await logger.logError(error, {
          method: 'POST',
          url: '/api/user/favorites/remove',
          errorType: 'remove_favorite_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

// === Утилиты ===

// Хук для проверки доступности контента
export const useContentAccess = () => {
  const { hasActiveSubscription } = useAppStore()
  const subscription = hasActiveSubscription()


  const canAccessCard = (card: { is_free?: boolean }) => {
    const canAccess = card.is_free || subscription

    return canAccess
  }

  const canAccessCategories = () => {
    return subscription
  }

  return {
    canAccessCard,
    canAccessCategories,
    hasSubscription: subscription
  }
}

// === Курсы ===

export const useCourses = () => {
  const { authStatus } = useAppStore()
  const isAuthComplete = authStatus === 'success'

  return useQuery<CoursesResponse, Error>(
    ['courses'],
    () => apiClient.getCourses(),
    {
      enabled: isAuthComplete,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 2,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: '/api/content/courses',
          errorType: 'courses_fetch_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

export const useCourse = (courseId: number | null) => {
  return useQuery<CourseDetailResponse, Error>(
    ['course', courseId],
    () => apiClient.getCourse(courseId!),
    {
      enabled: !!courseId,
      staleTime: 0,
      retry: 1,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: `/api/content/course/${courseId}`,
          errorType: 'course_fetch_error',
          additionalData: {
            courseId,
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

export const useCourseLanding = (courseId: number | null) => {
  return useQuery<CourseDetailResponse, Error>(
    ['courseLanding', courseId],
    () => apiClient.getCourseLanding(courseId!),
    {
      enabled: !!courseId,
      staleTime: 5 * 60 * 1000,
      retry: 1,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: `/api/content/course-landing/${courseId}`,
          errorType: 'course_landing_fetch_error',
          additionalData: {
            courseId,
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

export const useLesson = (lessonId: number | null) => {
  return useQuery<LessonDetailResponse, Error>(
    ['lesson', lessonId],
    () => apiClient.getLesson(lessonId!),
    {
      enabled: !!lessonId,
      staleTime: 10 * 60 * 1000,
      retry: 1,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: `/api/content/lesson/${lessonId}`,
          errorType: 'lesson_fetch_error',
          additionalData: {
            lessonId,
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

// === Fortune Wheel (Колесо Фортуны) ===

// Проверка возможности крутить колесо
export const useCanSpin = () => {
  const { authStatus } = useAppStore()
  const isAuthComplete = authStatus === 'success'

  return useQuery<CheckSpinResponse, Error>(
    ['canSpin'],
    () => apiClient.canSpin(),
    {
      enabled: isAuthComplete,
      staleTime: 30 * 1000, // 30 секунд
      cacheTime: 60 * 1000, // 1 минута
      retry: 1,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: '/api/spin/can-spin',
          errorType: 'can_spin_check_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

// Получить список секторов призов для текущей кампании
export const useSpinPrizes = () => {
  const { authStatus } = useAppStore()
  const isAuthComplete = authStatus === 'success'

  return useQuery<PrizesResponse, Error>(
    ['spinPrizes'],
    () => apiClient.getSpinPrizes(),
    {
      enabled: isAuthComplete,
      staleTime: 5 * 60 * 1000, // 5 минут
      cacheTime: 10 * 60 * 1000, // 10 минут
      retry: 2,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: '/api/spin/prizes',
          errorType: 'spin_prizes_fetch_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

// Получить список выигранных призов пользователя
export const useMyPrizes = () => {
  const { authStatus } = useAppStore()
  const isAuthComplete = authStatus === 'success'

  return useQuery<MyPrizesResponse, Error>(
    ['myPrizes'],
    () => apiClient.getMyPrizes(),
    {
      enabled: isAuthComplete,
      staleTime: 60 * 1000, // 1 минута
      cacheTime: 5 * 60 * 1000, // 5 минут
      retry: 1,
      onError: async (error) => {
        await logger.logError(error, {
          method: 'GET',
          url: '/api/spin/my-prizes',
          errorType: 'my_prizes_fetch_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

// Выполнить спин колеса фортуны
export const useSpin = () => {
  const queryClient = useQueryClient()

  return useMutation<SpinResponse, Error>(
    () => apiClient.spin(),
    {
      onSuccess: () => {
        // Инвалидируем кэш после успешного спина
        queryClient.invalidateQueries(['canSpin'])
        queryClient.invalidateQueries(['myPrizes'])
        queryClient.invalidateQueries(['spinPrizes'])
      },
      onError: async (error) => {
        console.error('Ошибка при спине:', error)
        await logger.logError(error, {
          method: 'POST',
          url: '/api/spin/spin',
          errorType: 'spin_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

// Сброс лимита спинов (для разработки)
export const useNextDay = () => {
  const queryClient = useQueryClient()

  return useMutation<CheckSpinResponse, Error>(
    () => apiClient.nextDay(),
    {
      onSuccess: () => {
        // Инвалидируем кэш после сброса лимита
        queryClient.invalidateQueries(['canSpin'])
        queryClient.invalidateQueries(['myPrizes'])
        queryClient.invalidateQueries(['spinPrizes'])
      },
      onError: async (error) => {
        console.error('Ошибка при сбросе лимита:', error)
        await logger.logError(error, {
          method: 'POST',
          url: '/api/spin/next_day',
          errorType: 'next_day_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
}

// Сброс всех наград пользователя (для разработки)
export const useResetRewards = () => {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean; message: string }, Error>(
    () => apiClient.resetRewards(),
    {
      onSuccess: () => {
        // Инвалидируем кэш после сброса наград
        queryClient.invalidateQueries(['myPrizes'])
        queryClient.invalidateQueries(['canSpin'])
        queryClient.invalidateQueries(['spinPrizes'])
      },
      onError: async (error) => {
        console.error('Ошибка при сбросе наград:', error)
        await logger.logError(error, {
          method: 'POST',
          url: '/api/spin/reset_rewards',
          errorType: 'reset_rewards_error',
          additionalData: {
            detail: (error as any)?.response?.data?.detail
          }
        })
      }
    }
  )
} 